import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getEmployerJobs, getJobStats, updateJobStatus, deleteJob } from '../services/jobService';
import { getCompanyProfileByUserId } from '../services/companyService';
import { getEmployerApplications, APPLICATION_STATUS } from '../services/applicationService';
import { requestNotificationPermission } from '../services/fcmService';
import { getUserActiveSubscription } from '../services/subscriptionService';
import JobCard from '../components/JobCard';
import LoadingSpinner from '../components/LoadingSpinner';
import Footer from '../components/Footer';
import ContentWithAds from '../components/ContentWithAds';

const EmployerDashboard = ({ user, userData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    draftJobs: 0,
    closedJobs: 0,
    expiredJobs: 0,
    totalApplications: 0,
    newApplications: 0,
    shortlistedCandidates: 0,
    totalViews: 0,
    avgViewsPerJob: 0,
    responseRate: 0,
    avgTimeToHire: 0
  });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [recentActivity, setRecentActivity] = useState([]);
  const [topPerformingJobs, setTopPerformingJobs] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [hasCompanyProfile, setHasCompanyProfile] = useState(false);
  const [companyProfileLoading, setCompanyProfileLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
      window.history.replaceState({}, document.title);
    }
    if (user?.uid) {
      fetchData();
      fetchCompanyProfile();
      // Initialize FCM in background - don't block page load
      setTimeout(() => initializeFCM(), 2000);
    }
  }, [location.state, user?.uid]);

  const initializeFCM = async () => {
    try {
      // Check if FCM token exists, if not request permission and save
      const result = await requestNotificationPermission(user.uid);
      if (result.success) {
        console.log('FCM token initialized successfully');
      } else {
        // Handle specific FCM errors gracefully
        if (result.code === 'VAPID_NOT_CONFIGURED') {
          console.warn('FCM not configured - notifications disabled');
        } else if (result.code === 'VAPID_AUTH_ERROR') {
          console.warn('FCM authentication error - notifications disabled');
        } else {
          console.warn('FCM initialization failed:', result.error);
        }
      }
    } catch (error) {
      // Silently handle FCM errors to prevent blocking page functionality
      if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        console.warn('FCM authentication failed - notifications disabled');
      } else {
        console.warn('FCM initialization error:', error.message);
      }
    }
  };

  const fetchData = async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const timeout = (ms) => new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), ms)
      );

      const promises = [
        Promise.race([
          getEmployerJobs(user.uid, { limit: 10 }),
          timeout(3000)
        ]).catch(err => ({ success: false, jobs: [], error: err.message })),

        Promise.race([
          getJobStats(user.uid),
          timeout(3000)
        ]).catch(err => ({ success: false, stats: {}, error: err.message })),

        Promise.race([
          getEmployerApplications(user.uid),
          timeout(3000)
        ]).catch(err => ({ success: false, applications: [], error: err.message })),

        Promise.race([
          getUserActiveSubscription(user.uid),
          timeout(3000)
        ]).catch(err => ({ success: false, subscription: null, error: err.message }))
      ];

      const [jobsResult, statsResult, applicationsResult, subscriptionResult] = await Promise.all(promises);

      if (subscriptionResult && subscriptionResult.success) {
        setSubscription(subscriptionResult.subscription);
      }

      if (jobsResult.success) {
        setJobs(jobsResult.jobs);

        if (applicationsResult.success) {
          const topJobs = processTopPerformingJobs(jobsResult.jobs, applicationsResult.applications);
          setTopPerformingJobs(topJobs);
        } else {
          const topJobs = processTopPerformingJobs(jobsResult.jobs, []);
          setTopPerformingJobs(topJobs);
        }
      } else {
        setJobs([]);
        setTopPerformingJobs([]);
      }

      // Initialize stats with default values
      let enhancedStats = {
        totalJobs: 0,
        activeJobs: 0,
        draftJobs: 0,
        closedJobs: 0,
        expiredJobs: 0,
        totalApplications: 0,
        newApplications: 0,
        shortlistedCandidates: 0,
        totalViews: 0,
        avgViewsPerJob: 0,
        responseRate: 0,
        avgTimeToHire: 0,
        pendingApplications: 0,
        reviewedApplications: 0,
        rejectedApplications: 0
      };

      if (statsResult.success) {
        enhancedStats = { ...enhancedStats, ...statsResult.stats };
      }

      if (applicationsResult.success) {
        const applicationStats = calculateApplicationStats(applicationsResult.applications);
        enhancedStats = {
          ...enhancedStats,
          ...applicationStats
        };

        const activity = generateRecentActivity(applicationsResult.applications, jobsResult.success ? jobsResult.jobs : []);
        setRecentActivity(activity);
      } else {
        setRecentActivity([]);
      }

      setStats(enhancedStats);

    } catch (error) {
      setStats({
        totalJobs: 0,
        activeJobs: 0,
        draftJobs: 0,
        closedJobs: 0,
        expiredJobs: 0,
        totalApplications: 0,
        newApplications: 0,
        shortlistedCandidates: 0,
        totalViews: 0,
        avgViewsPerJob: 0,
        responseRate: 0,
        avgTimeToHire: 0,
        pendingApplications: 0,
        reviewedApplications: 0,
        rejectedApplications: 0
      });
      setJobs([]);
      setRecentActivity([]);
      setTopPerformingJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateApplicationStats = (applications) => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const totalApplications = applications.length;

    const newApplications = applications.filter(app => {
      if (!app.appliedAt) return false;

      let appliedDate;
      if (typeof app.appliedAt === 'number') {
        appliedDate = new Date(app.appliedAt);
      } else if (typeof app.appliedAt.toDate === 'function') {
        appliedDate = app.appliedAt.toDate();
      } else if (app.appliedAt instanceof Date) {
        appliedDate = app.appliedAt;
      } else {
        return false;
      }

      return appliedDate >= oneWeekAgo;
    }).length;

    const shortlistedCandidates = applications.filter(app =>
      app.status === APPLICATION_STATUS.SHORTLISTED || app.status === 'selected'
    ).length;

    const pendingApplications = applications.filter(app =>
      app.status === APPLICATION_STATUS.PENDING
    ).length;

    const reviewedApplications = applications.filter(app =>
      app.status === APPLICATION_STATUS.REVIEWING
    ).length;

    const rejectedApplications = applications.filter(app =>
      app.status === APPLICATION_STATUS.REJECTED
    ).length;

    const responseRate = totalApplications > 0
      ? Math.round(((totalApplications - pendingApplications) / totalApplications) * 100)
      : 0;

    return {
      totalApplications,
      newApplications,
      shortlistedCandidates,
      pendingApplications,
      reviewedApplications,
      rejectedApplications,
      responseRate
    };
  };

  const generateRecentActivity = (applications, jobs) => {
    const activities = [];
    const now = new Date();

    const getDate = (dateValue) => {
      if (!dateValue) return null;
      if (typeof dateValue === 'number') return new Date(dateValue);
      if (typeof dateValue.toDate === 'function') return dateValue.toDate();
      if (dateValue instanceof Date) return dateValue;
      return null;
    };

    applications
      .filter(app => app.appliedAt)
      .map(app => ({ ...app, appliedDate: getDate(app.appliedAt) }))
      .filter(app => app.appliedDate)
      .sort((a, b) => b.appliedDate - a.appliedDate)
      .slice(0, 5)
      .forEach(app => {
        const timeDiff = now - app.appliedDate;
        const timeAgo = formatTimeAgo(timeDiff);

        activities.push({
          type: 'application',
          message: `New application from ${app.applicantName} for ${app.jobTitle}`,
          time: timeAgo,
          icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
          timestamp: app.appliedDate
        });
      });

    applications
      .filter(app => app.updatedAt && app.status !== APPLICATION_STATUS.PENDING)
      .map(app => ({ ...app, updatedDate: getDate(app.updatedAt) }))
      .filter(app => app.updatedDate)
      .sort((a, b) => b.updatedDate - a.updatedDate)
      .slice(0, 3)
      .forEach(app => {
        const timeDiff = now - app.updatedDate;
        const timeAgo = formatTimeAgo(timeDiff);

        let message = '';
        let icon = '';

        switch (app.status) {
          case APPLICATION_STATUS.SHORTLISTED:
            message = `${app.applicantName} shortlisted for ${app.jobTitle}`;
            icon = 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z';
            break;
          case 'selected':
            message = `${app.applicantName} selected for ${app.jobTitle}`;
            icon = 'M5 13l4 4L19 7';
            break;
          case APPLICATION_STATUS.REVIEWING:
            message = `${app.applicantName}'s application reviewed for ${app.jobTitle}`;
            icon = 'M15 12a3 3 0 11-6 0 3 3 0 016 0z';
            break;
          default:
            return;
        }

        activities.push({
          type: 'status_change',
          message,
          time: timeAgo,
          icon,
          timestamp: app.updatedDate
        });
      });

    jobs
      .filter(job => job.createdAt)
      .map(job => ({ ...job, createdDate: getDate(job.createdAt) }))
      .filter(job => job.createdDate)
      .sort((a, b) => b.createdDate - a.createdDate)
      .slice(0, 2)
      .forEach(job => {
        const timeDiff = now - job.createdDate;
        const timeAgo = formatTimeAgo(timeDiff);

        activities.push({
          type: 'job_post',
          message: `New job posted: ${job.title}`,
          time: timeAgo,
          icon: 'M12 6v6m0 0v6m0-6h6m-6 0H6',
          timestamp: job.createdDate
        });
      });

    const sortedActivities = activities
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 6);

    return sortedActivities;
  };

  const processTopPerformingJobs = (jobs, applications) => {
    const jobPerformance = jobs.map(job => {
      const jobApplications = applications.filter(app => app.jobId === job.id);
      const shortlistedCount = jobApplications.filter(app =>
        app.status === APPLICATION_STATUS.SHORTLISTED || app.status === 'selected'
      ).length;

      return {
        ...job,
        applicationCount: jobApplications.length,
        shortlistedCount,
        viewCount: job.viewCount || 0,
        performanceScore: (jobApplications.length * 2) + (shortlistedCount * 5) + (job.viewCount || 0)
      };
    });

    const topJobs = jobPerformance
      .sort((a, b) => b.performanceScore - a.performanceScore)
      .slice(0, 5);

    return topJobs;
  };

  const formatTimeAgo = (timeDiff) => {
    const minutes = Math.floor(timeDiff / (1000 * 60));
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
      return minutes <= 1 ? 'Just now' : `${minutes} minutes ago`;
    } else if (hours < 24) {
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    } else {
      return days === 1 ? '1 day ago' : `${days} days ago`;
    }
  };

  const fetchCompanyProfile = async () => {
    setCompanyProfileLoading(true);
    try {
      const result = await getCompanyProfileByUserId(user.uid);

      if (result.success && result.data) {
        setCompanyProfile(result.data);
        setHasCompanyProfile(true);
      } else {
        setHasCompanyProfile(false);
      }
    } catch (error) {
      setHasCompanyProfile(false);
    } finally {
      setCompanyProfileLoading(false);
    }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      const result = await updateJobStatus(jobId, newStatus, user.uid);
      if (result.success) {
        setJobs(jobs.map(job =>
          job.id === jobId ? { ...job, status: newStatus } : job
        ));
        fetchData(); // Refresh stats
      } else {
        alert('Error updating job status: ' + result.error);
      }
    } catch (error) {
      alert('Error updating job status: ' + error.message);
    }
  };

  const handleDeleteJob = async (jobId) => {
    try {
      const result = await deleteJob(jobId, user.uid);
      if (result.success) {
        setJobs(jobs.filter(job => job.id !== jobId));
        fetchData(); // Refresh stats
      } else {
        alert('Error deleting job: ' + result.error);
      }
    } catch (error) {
      alert('Error deleting job: ' + error.message);
    }
  };

  const handlePostJob = () => {
    if (!hasCompanyProfile) {
      if (window.confirm('You need to create a company profile before posting jobs. Would you like to create one now?')) {
        navigate('/company-profile');
      }
      return;
    }
    navigate('/post-job');
  };

  const handleManageCompanyProfile = () => {
    navigate('/company-profile');
  };

  const handleNavigateToJobs = (filter = 'all') => {
    navigate('/manage-jobs', { state: { filter } });
  };

  const handleNavigateToApplications = (filter = 'all') => {
    let mappedFilter = filter;
    if (filter === 'shortlisted') {
      mappedFilter = APPLICATION_STATUS.SHORTLISTED;
    } else if (filter === 'selected') {
      mappedFilter = 'selected';
    } else if (filter === 'pending') {
      mappedFilter = APPLICATION_STATUS.PENDING;
    } else if (filter === 'reviewing') {
      mappedFilter = APPLICATION_STATUS.REVIEWING;
    } else if (filter === 'rejected') {
      mappedFilter = APPLICATION_STATUS.REJECTED;
    }

    navigate('/applications', { state: { filter: mappedFilter } });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="min-h-screen bg-slate-50/50 relative overflow-hidden pb-24 lg:pb-0">
        {/* Background grid pattern */}
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(#0e3a61_1px,transparent_1px)] [background-size:24px_24px] opacity-5"></div>

        <ContentWithAds>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
            
            {/* Top Layout Grid (Main & Sidebar) */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              
              {/* Left Column (Main Panel) */}
              <div className="lg:col-span-8 space-y-8">
                
                {/* Welcome Banner */}
                <div className="bg-gradient-to-br from-[#0e3a61] to-[#051728] rounded-[2rem] rounded-tr-[4rem] rounded-bl-[4rem] shadow-xl border border-[#0e3a61]/10 overflow-hidden relative p-8">
                  {/* Glowing background highlights */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-20 translate-x-20 pointer-events-none"></div>
                  <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-[#e2822f]/10 rounded-full blur-2xl pointer-events-none"></div>

                  <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-6 z-10">
                    <div className="flex-1 min-w-0">
                      <span className="text-[10px] uppercase font-bold tracking-widest text-[#e2822f] bg-[#e2822f]/15 px-3 py-1 rounded-full border border-[#e2822f]/20">
                        Talent Acquisition Workspace
                      </span>
                      <h1 className="text-2xl sm:text-3xl font-extrabold text-white mt-3 tracking-tight">
                        Welcome, Partner {userData?.firstName}!
                      </h1>
                      <p className="mt-2 text-slate-300 text-xs sm:text-sm max-w-xl leading-relaxed">
                        Enterprise command center for talent pipeline architecture, requisition tracking, and application intelligence.
                      </p>
                    </div>
                    <button
                      onClick={handlePostJob}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#e2822f] to-[#d97706] text-white font-bold text-sm rounded-xl hover:shadow-lg hover:shadow-[#e2822f]/20 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 flex-shrink-0"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span>Create Requisition</span>
                    </button>
                  </div>
                </div>

                {/* Asymmetric Stats Matrix */}
                <div>
                  <h2 className="text-xs uppercase font-bold tracking-widest text-slate-400 mb-4">Pipeline Intelligence Snapshot</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    
                    {/* Active Jobs Card (Dark Highlight Card) */}
                    <button
                      onClick={() => handleNavigateToJobs('active')}
                      className="bg-gradient-to-br from-[#0e3a61] to-[#08233c] text-white rounded-[2rem] rounded-tr-[3.5rem] rounded-bl-[3.5rem] shadow-xl p-6 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 text-left w-full relative overflow-hidden group border border-white/5"
                    >
                      <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -translate-y-8 translate-x-8 pointer-events-none"></div>
                      <div className="flex justify-between items-start h-full flex-col min-h-[100px]">
                        <div className="flex justify-between items-center w-full">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">Published Opportunities</span>
                          <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </div>
                        </div>
                        <div>
                          <p className="text-4xl font-extrabold tracking-tight mt-4">{stats.activeJobs}</p>
                          <p className="text-xs text-slate-400 mt-1">Actively sourcing elite candidates</p>
                        </div>
                      </div>
                    </button>

                    {/* Active Proposals (L-border Highlight Card) */}
                    <button
                      onClick={() => handleNavigateToApplications('all')}
                      className="bg-white text-slate-900 rounded-[2rem] rounded-tr-[3.5rem] rounded-bl-[3.5rem] shadow-xl p-6 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 text-left w-full border-l-4 border-l-[#e2822f] relative overflow-hidden group"
                    >
                      <div className="flex justify-between items-start h-full flex-col min-h-[100px]">
                        <div className="flex justify-between items-center w-full">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Active Proposals</span>
                          <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <svg className="w-5 h-5 text-[#e2822f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                        </div>
                        <div>
                          <p className="text-4xl font-extrabold tracking-tight text-slate-950 mt-4">{stats.totalApplications || 0}</p>
                          <p className="text-xs text-slate-500 mt-1">Aggregated historical applications</p>
                        </div>
                      </div>
                    </button>

                    {/* Pre-published Posts (Bottom Border) */}
                    <button
                      onClick={() => handleNavigateToJobs('draft')}
                      className="bg-white text-slate-900 rounded-[2rem] rounded-tr-[3.5rem] rounded-bl-[3.5rem] shadow-xl p-6 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 text-left w-full border-b-4 border-b-[#0e3a61] relative overflow-hidden group"
                    >
                      <div className="flex justify-between items-start h-full flex-col min-h-[100px]">
                        <div className="flex justify-between items-center w-full">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Pre-published Posts</span>
                          <div className="w-10 h-10 bg-[#0e3a61]/5 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <svg className="w-5 h-5 text-[#0e3a61]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </div>
                        </div>
                        <div>
                          <p className="text-4xl font-extrabold tracking-tight text-slate-950 mt-4">{stats.draftJobs}</p>
                          <p className="text-xs text-slate-500 mt-1">Staged in active drafts</p>
                        </div>
                      </div>
                    </button>

                    {/* Selected Candidates (Clean Slate) */}
                    <button
                      onClick={() => handleNavigateToApplications('shortlisted')}
                      className="bg-slate-50 text-slate-900 rounded-[2rem] rounded-tr-[3.5rem] rounded-bl-[3.5rem] shadow-lg p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-left w-full border border-slate-200 relative overflow-hidden group"
                    >
                      <div className="flex justify-between items-start h-full flex-col min-h-[100px]">
                        <div className="flex justify-between items-center w-full">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Selected Candidates</span>
                          <div className="w-10 h-10 bg-[#508d0c]/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <svg className="w-5 h-5 text-[#508d0c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                        </div>
                        <div>
                          <p className="text-4xl font-extrabold tracking-tight text-slate-950 mt-4">{stats.shortlistedCandidates || 0}</p>
                          <p className="text-xs text-slate-500 mt-1">Flagged for executive routing</p>
                        </div>
                      </div>
                    </button>

                  </div>
                </div>

                {/* Tab Navigation */}
                <div className="bg-slate-100/80 p-1.5 rounded-[1.5rem] border border-slate-200/50 backdrop-blur-sm flex gap-2 overflow-x-auto">
                  {[
                    { id: 'overview', name: 'Overview Console', icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2v0a2 2 0 012-2h6l2 2h6a2 2 0 012 2v1' },
                    { id: 'jobs', name: 'Requisition Manager', icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
                    { id: 'applications', name: 'Applicant Routing', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
                    { id: 'analytics', name: 'Growth Analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 py-3 px-4 text-xs font-bold rounded-xl flex items-center justify-center gap-2 transition-all duration-300 whitespace-nowrap ${activeTab === tab.id
                        ? 'bg-white text-[#0e3a61] shadow-md border border-slate-100'
                        : 'text-slate-600 hover:text-slate-900 border border-transparent'
                        }`}
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                      </svg>
                      <span>{tab.name}</span>
                    </button>
                  ))}
                </div>

                {/* Tab Content Panel */}
                <div className="bg-white rounded-[2rem] p-6 sm:p-8 shadow-xl border border-slate-100/50">
                  {activeTab === 'overview' && (
                    <div className="space-y-8">
                      {/* Recent Activity Log */}
                      <div>
                        <div className="flex items-center justify-between mb-6">
                          <div>
                            <h3 className="text-lg font-extrabold text-slate-950">Real-time Activity Logs</h3>
                            <p className="text-xs text-slate-500">Live operational events from talent pools</p>
                          </div>
                          <button
                            onClick={() => handleNavigateToApplications('all')}
                            className="text-[#0e3a61] hover:text-[#e2822f] text-xs font-bold flex items-center gap-1 transition-colors"
                          >
                            <span>Inspect All</span>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </div>

                        <div className="space-y-3">
                          {recentActivity.length > 0 ? (
                            recentActivity.map((activity, index) => (
                              <div key={index} className="flex items-center gap-4 p-4 bg-slate-50 hover:bg-slate-100/70 border border-slate-100 rounded-2xl transition-all duration-200">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${activity.type === 'application' ? 'bg-[#0e3a61]/10 text-[#0e3a61]' :
                                  activity.type === 'status_change' ? 'bg-[#508d0c]/10 text-[#508d0c]' :
                                    activity.type === 'job_post' ? 'bg-orange-100 text-[#e2822f]' :
                                      'bg-slate-200 text-slate-600'
                                  }`}>
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={activity.icon} />
                                  </svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-slate-900 font-semibold text-sm truncate">{activity.message}</p>
                                  <p className="text-slate-500 text-xs mt-0.5">{activity.time}</p>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-12 bg-slate-50 rounded-2xl border border-slate-100 border-dashed">
                              <div className="text-slate-300 mb-3">
                                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <p className="text-slate-800 font-bold text-sm">No activity recorded</p>
                              <p className="text-slate-400 text-xs mt-1 max-w-sm mx-auto">Events will display as candidates apply and status flags change.</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Leaderboard Requisitions */}
                      <div>
                        <div className="flex items-center justify-between mb-6">
                          <div>
                            <h3 className="text-lg font-extrabold text-slate-950">High Engagement Listings</h3>
                            <p className="text-xs text-slate-500">Requisitions yielding optimal traffic volume</p>
                          </div>
                        </div>

                        <div className="space-y-4">
                          {topPerformingJobs.length > 0 ? (
                            topPerformingJobs.slice(0, 3).map((job, index) => (
                              <div key={job.id} className="p-5 bg-slate-50 rounded-2xl border border-slate-100 relative overflow-hidden group hover:shadow-md transition-all duration-300">
                                <div className="absolute top-0 left-0 bottom-0 w-1.5 bg-[#0e3a61]"></div>
                                <div className="flex items-start justify-between mb-3 pl-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold bg-[#0e3a61]/10 text-[#0e3a61] px-2 py-0.5 rounded-md">Rank #{index + 1}</span>
                                    <h4 className="font-extrabold text-slate-950 text-sm sm:text-base">{job.title}</h4>
                                  </div>
                                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${job.status === 'active' ? 'bg-[#508d0c]/10 text-[#508d0c] border border-[#508d0c]/20' :
                                    job.status === 'draft' ? 'bg-amber-100 text-amber-800 border border-amber-200' :
                                      'bg-slate-100 text-slate-800 border border-slate-200'
                                    }`}>
                                    {job.status}
                                  </span>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-center text-xs text-slate-600 pl-2 mt-4 bg-white p-3 rounded-xl border border-slate-100 shadow-sm">
                                  <div>
                                    <p className="font-bold text-slate-950">{job.applicationCount || 0}</p>
                                    <p className="text-[10px] text-slate-500">Proposals</p>
                                  </div>
                                  <div className="border-x border-slate-100">
                                    <p className="font-bold text-slate-950">{job.viewCount || 0}</p>
                                    <p className="text-[10px] text-slate-500">Impressions</p>
                                  </div>
                                  <div>
                                    <p className="font-bold text-slate-950">{job.shortlistedCount || 0}</p>
                                    <p className="text-[10px] text-slate-500">Flagged</p>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-12 bg-slate-50 rounded-2xl border border-slate-100 border-dashed">
                              <div className="text-slate-300 mb-3">
                                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                              </div>
                              <p className="text-slate-800 font-bold text-sm">No analytics generated</p>
                              <p className="text-slate-400 text-xs mt-1 max-w-sm mx-auto">Active job performance indicators will display here.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'jobs' && (
                    <div className="space-y-6">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-100">
                        <div>
                          <h3 className="text-lg font-extrabold text-slate-950">Active Requisitions</h3>
                          <p className="text-xs text-slate-500">Track and manage your published listings</p>
                        </div>
                        <div className="flex gap-3 w-full sm:w-auto">
                          <button
                            onClick={handlePostJob}
                            className="flex-1 sm:flex-none bg-[#0e3a61] hover:bg-[#0b2e4e] text-white px-4 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 shadow-sm transition-colors duration-200"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            <span>Create Requisition</span>
                          </button>
                          <Link
                            to="/manage-jobs"
                            className="flex-1 sm:flex-none text-[#0e3a61] hover:text-[#e2822f] font-bold text-xs flex items-center justify-center gap-1 px-4 py-2.5 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all duration-200"
                          >
                            <span>Inspect All</span>
                          </Link>
                        </div>
                      </div>

                      {jobs.length === 0 ? (
                        <div className="text-center py-16">
                          <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-slate-100 border-dashed">
                            <svg className="w-10 h-10 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <h4 className="text-base font-bold text-slate-900 mb-2">No requisitions created</h4>
                          <p className="text-xs text-slate-500 mb-6 max-w-sm mx-auto">Launch your first job posting to start gathering elite responses.</p>
                          <button
                            onClick={handlePostJob}
                            className="bg-[#0e3a61] hover:bg-[#0b2e4e] text-white px-6 py-3 rounded-xl font-bold text-sm flex items-center gap-2 mx-auto shadow-md transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            <span>Deploy First Requisition</span>
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {jobs.map((job) => (
                            <JobCard
                              key={job.id}
                              job={job}
                              showActions={true}
                              onStatusChange={handleStatusChange}
                              onDelete={handleDeleteJob}
                            />
                          ))}
                          {jobs.length >= 10 && (
                            <div className="text-center pt-6 border-t border-slate-100">
                              <Link
                                to="/manage-jobs"
                                className="inline-flex items-center gap-2 text-[#0e3a61] hover:text-[#e2822f] font-bold text-sm transition-colors"
                              >
                                <span>Inspect All Requisitions</span>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                              </Link>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'applications' && (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-slate-100 border-dashed">
                        <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">Proposal Management</h3>
                      <p className="text-xs text-slate-500 mb-6 max-w-sm mx-auto">Inspect profile diagnostics, filter resumes, and shortlist matches in full detail.</p>
                      <Link
                        to="/applications"
                        className="inline-flex items-center gap-2 bg-[#0e3a61] hover:bg-[#0b2e4e] text-white px-6 py-3 rounded-xl font-bold text-sm shadow-md transition-colors"
                      >
                        <span>Launch Applicant Console</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </Link>
                    </div>
                  )}

                  {activeTab === 'analytics' && (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-slate-100 border-dashed">
                        <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">Advanced Performance Indicators</h3>
                      <p className="text-xs text-slate-500 mb-6 max-w-sm mx-auto">Gain insight on conversion rates, impression trends, and target demographic details.</p>
                      <button className="inline-flex items-center gap-2 bg-[#0e3a61] hover:bg-[#0b2e4e] text-white px-6 py-3 rounded-xl font-bold text-sm shadow-md transition-colors">
                        <span>Initiate Analytics Stream</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>

              </div>

              {/* Right Column (Sidebar Panel) */}
              <div className="lg:col-span-4 space-y-8">
                
                {/* Profile Success / Setup Card */}
                {!companyProfileLoading && hasCompanyProfile && companyProfile ? (
                  <div className="bg-white rounded-[2rem] shadow-xl border border-slate-100 p-6 border-l-4 border-l-[#0e3a61] relative overflow-hidden group">
                    <h3 className="text-xs uppercase font-extrabold tracking-wider text-slate-400 mb-4">Corporate Identity Profile</h3>
                    <div className="flex items-center gap-4">
                      {companyProfile.logoUrl ? (
                        <img
                          src={companyProfile.logoUrl}
                          alt={companyProfile.companyName}
                          className="w-14 h-14 rounded-2xl object-contain bg-slate-50 p-2 border border-slate-100 shadow-inner flex-shrink-0"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-2xl bg-[#0e3a61]/5 flex items-center justify-center flex-shrink-0 text-[#0e3a61] font-black text-xl border border-slate-100 shadow-inner">
                          {companyProfile.companyName.charAt(0)}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h4 className="text-base font-extrabold text-slate-900 truncate">{companyProfile.companyName}</h4>
                        <p className="text-xs text-slate-500 truncate mt-0.5">{companyProfile.industry}</p>
                        <p className="text-[10px] text-slate-400 font-medium truncate mt-1">{companyProfile.companySize} employees</p>
                      </div>
                    </div>
                    
                    <button
                      onClick={handleManageCompanyProfile}
                      className="w-full mt-6 py-2.5 bg-slate-50 hover:bg-slate-100 text-[#0e3a61] hover:text-[#e2822f] border border-slate-200 font-bold text-xs rounded-xl transition-all duration-200 flex items-center justify-center gap-1.5"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span>Modify Identity Dossier</span>
                    </button>
                  </div>
                ) : (
                  !companyProfileLoading && (
                    <div className="bg-white rounded-[2rem] shadow-xl border border-orange-200/60 p-6 border-l-4 border-l-[#e2822f] relative overflow-hidden group">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center flex-shrink-0 text-[#e2822f]">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-slate-900 font-extrabold text-sm mb-1">Finalize Corporate Identity Dossier</h3>
                          <p className="text-slate-500 text-xs leading-relaxed mb-4">
                            Design and register your corporate parameters to enable requisition placement and candidate filtering.
                          </p>
                          <button
                            onClick={handleManageCompanyProfile}
                            className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-[#e2822f] to-[#d97706] text-white font-bold text-xs rounded-xl shadow-md shadow-orange-500/10 hover:shadow-orange-500/20 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            <span>Establish Dossier</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                )}

                {/* Subscription Status Card */}
                <div className="bg-gradient-to-br from-[#0e3a61] to-[#051728] text-white rounded-[2rem] shadow-xl p-6 relative overflow-hidden border border-white/5">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16 pointer-events-none"></div>
                  
                  <div className="relative z-10 flex justify-between items-center mb-6">
                    <h3 className="text-xs uppercase font-extrabold tracking-wider text-slate-300">Access Rights & Quota</h3>
                    <span className="px-3 py-1 bg-white/10 text-white text-[10px] font-extrabold rounded-full border border-white/20 uppercase tracking-widest">
                      {subscription ? subscription.packageName : 'Free Tier'}
                    </span>
                  </div>

                  {subscription ? (
                    <div className="relative z-10 space-y-5">
                      <div>
                        <div className="flex justify-between text-xs mb-1.5">
                          <span className="text-slate-300">Job Posting Limits</span>
                          <span className="font-bold text-white">
                            {subscription.usageStats?.jobPostingsUsed || 0} / {subscription.maxJobPostings === 999999 ? '∞' : subscription.maxJobPostings}
                          </span>
                        </div>
                        <div className="w-full bg-white/10 rounded-full h-1.5">
                          <div
                            className="bg-[#e2822f] h-1.5 rounded-full transition-all duration-500"
                            style={{ width: `${subscription.maxJobPostings === 999999 ? 0 : Math.min(((subscription.usageStats?.jobPostingsUsed || 0) / (subscription.maxJobPostings || 1)) * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between text-xs mb-1.5">
                          <span className="text-slate-300">Featured Job Spots</span>
                          <span className="font-bold text-white">
                            {subscription.usageStats?.featuredJobsUsed || 0} / {subscription.featuredJobs === 999999 ? '∞' : subscription.featuredJobs}
                          </span>
                        </div>
                        <div className="w-full bg-white/10 rounded-full h-1.5">
                          <div
                            className="bg-[#e2822f] h-1.5 rounded-full transition-all duration-500"
                            style={{ width: `${subscription.featuredJobs === 999999 ? 0 : Math.min(((subscription.usageStats?.featuredJobsUsed || 0) / (subscription.featuredJobs || 1)) * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-white/10 flex items-center justify-between text-xs">
                        <span className="text-slate-400">Expiration Date</span>
                        <span className="font-bold text-white">
                          {subscription.endDate?.toDate ? subscription.endDate.toDate().toLocaleDateString() : new Date(subscription.endDate).toLocaleDateString()}
                        </span>
                      </div>

                      <button
                        onClick={() => navigate('/packages')}
                        className="w-full mt-4 py-2.5 bg-white/10 hover:bg-white/20 text-white font-bold text-xs rounded-xl transition-all duration-200 border border-white/15"
                      >
                        Optimize Access Rights
                      </button>
                    </div>
                  ) : (
                    <div className="relative z-10 text-center py-4">
                      <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/10 shadow-inner text-white">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                      </div>
                      <p className="text-xs text-slate-300 leading-relaxed mb-5">You are utilizing restricted platform access. Upgrade to post additional positions.</p>
                      <button
                        onClick={() => navigate('/packages')}
                        className="w-full py-2.5 bg-[#e2822f] hover:bg-[#c97024] text-white font-bold text-xs rounded-xl shadow-md shadow-orange-500/10 hover:shadow-orange-500/20 transition-all duration-200"
                      >
                        Explore Access Limits
                      </button>
                    </div>
                  )}
                </div>

                {/* Banner Message Alert */}
                {message && (
                  <div className="bg-[#508d0c]/10 rounded-[2rem] border border-[#508d0c]/20 p-5 flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#508d0c]/20 rounded-xl flex items-center justify-center text-[#508d0c] flex-shrink-0">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-slate-900 font-bold text-xs uppercase tracking-wider mb-1">Status Notification</h4>
                      <p className="text-slate-700 text-xs font-semibold leading-relaxed">{message}</p>
                    </div>
                  </div>
                )}

              </div>

            </div>

          </div>
        </ContentWithAds>
      </div>

      {/* Footer - Hidden on mobile */}
      <div className="hidden lg:block">
        <Footer />
      </div>
    </>
  );
};

export default EmployerDashboard;